import { computed, reactive } from 'vue';

export type Identifier = string | number;

export interface BaseAnswer {
  id: Identifier;
}

export type AnswerMap<T extends BaseAnswer> = Map<Identifier, T>;
export interface KeyedAnswers<T extends BaseAnswer = BaseAnswer> {
  [questionId: Identifier]: AnswerMap<T>;
}

export interface Question<T extends BaseAnswer = BaseAnswer> {
  answers: T[];
  id: Identifier;
  maxSelections?: number;
}

export function arrayFromMap<T extends BaseAnswer>(map: AnswerMap<T>) {
  return Array.from(map.values());
}

export const useQuiz = <T extends BaseAnswer, U extends Question<T>>(questions: U[]) => {
  const selectedAnswers: KeyedAnswers<T> = reactive(
    Object.fromEntries(questions.map((q) => [q.id, new Map<Identifier, T>()])),
  );

  const lastSelectedAnswerByQuestion: Map<Identifier, U['answers'][number] | undefined> = reactive(
    new Map(questions.map((q) => [q.id, undefined])),
  );

  const questionsWithSelectedAnswers = computed<U[]>(() =>
    Object.values(selectedAnswers).map((answersMap, i) => ({
      ...questions[i],
      answers: arrayFromMap(answersMap),
    })),
  );

  const surveySubmissions = computed(() =>
    Object.values(selectedAnswers).flatMap((answers) =>
      arrayFromMap(answers).map((answer) => ({
        answerId: String(answer.id),
      })),
    ),
  );

  function toggleAnswer(question: U, answer: T) {
    const { maxSelections = Infinity } = question;
    const currentSelection = selectedAnswers[question.id];
    if (!currentSelection) {
      throw new Error(`Quiz state did not initialize for question ${question.id}`);
    }

    if (currentSelection.has(answer.id)) {
      currentSelection.delete(answer.id);
      lastSelectedAnswerByQuestion.set(question.id, arrayFromMap(currentSelection).pop());
    } else if (maxSelections === 1) {
      currentSelection.clear();
      currentSelection.set(answer.id, answer);
      lastSelectedAnswerByQuestion.set(question.id, answer);
    } else if (currentSelection.size < maxSelections) {
      currentSelection.set(answer.id, answer);
      lastSelectedAnswerByQuestion.set(question.id, answer);
    }
  }

  return {
    lastSelectedAnswerByQuestion,
    questionsWithSelectedAnswers,
    selectedAnswers,
    surveySubmissions,
    toggleAnswer,
  };
};
